<template>
  <a-watermark content="左手的亚朵酒店">
    <div class="page">
      <!-- 顶栏 -->
      <header class="topbar">
        <div class="topbar-inner">
          <div class="brand">
            <span class="brand-logo">🏨</span>
            <div class="brand-text">
              <h1>亚朵速查</h1>
              <span class="brand-sub">多账号聚合查价 · 报价文案生成</span>
            </div>
          </div>
          <a-button class="cfg-btn" type="text" @click="toggleConfig">
            <span style="font-size:16px">⚙️</span>
            <span class="cfg-btn-text">配置</span>
          </a-button>
        </div>
      </header>

      <main class="content">
        <!-- 配置区 -->
        <a-collapse v-model:activeKey="configOpen" :bordered="false" class="cfg-collapse">
          <a-collapse-panel key="cfg" header="⚙️ 配置 · Token / 报价公式 / 文案模板">
            <a-row :gutter="[16, 16]">
              <a-col :xs="24" :md="12">
                <div class="cfg-label">查价 Token <span class="cfg-badge">多账号，每行一个</span></div>
                <a-textarea
                  v-model:value="queryTokenText"
                  :rows="4"
                  placeholder="账号1的token&#10;账号2的token&#10;..."
                />
                <div class="cfg-hint">已配置 <b>{{ queryTokens.length }}</b> 个查价账号</div>
              </a-col>
              <a-col :xs="24" :md="12">
                <div class="cfg-label">弹窗检测 Token <span class="cfg-badge cfg-badge-warn">勿与查价混用</span></div>
                <a-textarea
                  v-model:value="popupTokenText"
                  :rows="4"
                  placeholder="专用于弹窗检测的token&#10;..."
                />
                <div class="cfg-hint">已配置 <b>{{ popupTokens.length }}</b> 个弹窗检测账号</div>
              </a-col>
            </a-row>

            <a-divider class="cfg-divider">报价公式 &nbsp;报价 = 门市价 × k + b</a-divider>
            <a-row :gutter="[16, 16]">
              <a-col :xs="24" :md="12">
                <div class="cfg-box">
                  <div class="cfg-label">非弹窗报价 <span class="cfg-hint">未触发新客弹窗时</span></div>
                  <div class="cfg-formula">
                    <label>k</label>
                    <a-input-number v-model:value="pricing.nonPopupK" :step="0.01" style="width:96px" />
                    <label>b</label>
                    <a-input-number v-model:value="pricing.nonPopupB" :step="1" style="width:96px" />
                  </div>
                </div>
              </a-col>
              <a-col :xs="24" :md="12">
                <div class="cfg-box cfg-box-warn">
                  <div class="cfg-label">弹窗报价 <span class="cfg-hint">触发新客弹窗时</span></div>
                  <div class="cfg-formula">
                    <label>k</label>
                    <a-input-number v-model:value="pricing.popupK" :step="0.01" style="width:96px" />
                    <label>b</label>
                    <a-input-number v-model:value="pricing.popupB" :step="1" style="width:96px" />
                  </div>
                </div>
              </a-col>
            </a-row>

            <a-divider class="cfg-divider">报价文案模板</a-divider>
            <a-textarea v-model:value="template" :rows="6" />
            <div class="cfg-hint" style="margin-top:8px">
              可用变量：<code>{hotelName}</code> <code>{address}</code> <code>{checkInDate}</code>
              <code>{checkOutDate}</code> <code>{nightCount}</code> <code>{rooms}</code>
              <code>{coupons}</code> <code>{popupTag}</code>
            </div>
            <a-button size="small" style="margin-top:8px" @click="template = DEFAULT_TEMPLATE">恢复默认模板</a-button>
          </a-collapse-panel>
        </a-collapse>

        <!-- 查询条件 -->
        <section class="query-bar">
          <a-select
            v-model:value="chainId"
            show-search
            placeholder="🔍 输入地址或酒店名搜索"
            class="query-select"
            size="large"
            :default-active-first-option="false"
            :show-arrow="false"
            :filter-option="false"
            :not-found-content="searching ? '搜索中...' : null"
            :options="searchData"
            @search="handleSearch"
          />
          <a-range-picker
            :inputReadOnly="true"
            v-model:value="date"
            format="YYYY-MM-DD"
            size="large"
            class="query-date"
          />
          <div class="query-actions">
            <a-button type="primary" size="large" :loading="loading" :disabled="!chainId" @click="refreshDetail">
              查价
            </a-button>
            <a-button size="large" :disabled="!roomUrl" @click="openUrl(roomUrl)">官网</a-button>
          </div>
        </section>
        <div v-if="chainData.address" class="query-addr">📍 {{ chainData.address }}</div>

        <!-- 优惠券 Banner -->
        <a-alert
          v-if="hasQueried && coupons.length"
          type="success"
          show-icon
          class="coupon-banner"
        >
          <template #message>
            <span class="coupon-banner-title">🎟️ 检测到 {{ coupons.length }} 张优惠券</span>
          </template>
          <template #description>
            <div class="coupon-chips">
              <div v-for="(c, i) in coupons" :key="i" class="coupon-chip" :class="{ 'is-disabled': !c.canGet }">
                <span class="coupon-chip-name">{{ c.name }}</span>
                <span v-if="c.value" class="coupon-chip-value">¥{{ c.value }}</span>
                <a-tag :color="c.canGet ? 'green' : 'default'" :bordered="false">{{ c.canGet ? '可领' : '不可领' }}</a-tag>
                <span v-if="c.expiry || c.roomTypeLimit" class="coupon-chip-meta">{{ [c.expiry, c.roomTypeLimit].filter(Boolean).join(' · ') }}</span>
              </div>
            </div>
          </template>
        </a-alert>
        <a-alert
          v-else-if="hasQueried"
          type="info"
          show-icon
          message="🎟️ 优惠券检测：暂无可用优惠券"
          class="coupon-banner"
        />

        <!-- 状态条 -->
        <div v-if="hasQueried || loading" class="status-bar">
          <a-tag v-if="popupChecked" :color="hasPopup ? 'error' : 'success'" :bordered="false">
            <h1>{{ hasPopup ? '⚠️ 新客弹窗告警' : '✅ 无新客弹窗' }}</h1>
          </a-tag>
          <a-tag v-else-if="loading" color="processing" :bordered="false">弹窗检测中…</a-tag>
          <a-tag v-else-if="popupError" color="warning" :bordered="false">弹窗检测失败 · 默认非弹窗公式</a-tag>
          <template v-if="rooms.length">
            <a-divider type="vertical" />
            <span class="cfg-hint">手动切换：</span>
            <a-switch
              v-model:checked="hasPopup"
              checked-children="弹窗公式"
              un-checked-children="非弹窗公式"
            />
          </template>
        </div>

        <!-- 结果区 -->
        <a-row :gutter="[16, 16]" class="result-row">
          <!-- 左：价格表 -->
          <a-col :xs="24" :lg="15">
            <a-card :bordered="false" class="panel" :body-style="{ padding: '4px 0' }">
              <template #title><span class="panel-title">🛏️ 房型价格（共 {{ rooms.length }} 种 · 点击展开各账号策略）</span></template>
              <a-table
                :columns="columns"
                :data-source="rooms"
                :pagination="false"
                :row-key="(r) => r.roomTypeId || r.roomTypeName"
                :expand-row-by-click="true"
                :scroll="{ x: 'max-content' }"
                size="middle"
              >
                <template #emptyText>
                  <a-empty :image="false" :description="loading ? '查价中…' : '请先查价'" />
                </template>
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'room'">
                    <div class="room-name">
                      <strong>{{ record.roomTypeName }}</strong>
                      <a-tag v-if="record.canUpgrade" color="purple" :bordered="false">可升房</a-tag>
                    </div>
                    <div class="room-sub">{{ record.rates.length }} 个策略</div>
                  </template>
                  <template v-else-if="column.key === 'market'"><span class="muted">¥{{ record.marketPrice }}</span></template>
                  <template v-else-if="column.key === 'lowest'">¥{{ record.lowestPrice }}</template>
                  <template v-else-if="column.key === 'quote'">
                    <span class="quote-price">¥{{ quoteOf(record) }}</span>
                  </template>
                  <template v-else-if="column.key === 'stock'">
                    <a-tag :color="record.stock >= 3 ? 'success' : 'orange'" :bordered="false">
                      {{ record.stock >= 3 ? '有房' : '剩' + record.stock + '间' }}
                    </a-tag>
                  </template>
                  <template v-else-if="column.key === 'action'">
                    <a-button type="link" size="small" @click.stop="copyRoom(record)">复制</a-button>
                  </template>
                </template>

                <!-- 展开：价格策略，按价格升序 -->
                <template #expandedRowRender="{ record }">
                  <div class="rate-list">
                    <div v-for="(item, idx) in record.rates" :key="idx" class="rate-item">
                      <a-tag color="blue" :bordered="false">{{ item.account }}</a-tag>
                      <span class="rate-name">{{ item.name }}</span>
                      <strong class="quote-price rate-price">¥{{ item.price }}</strong>
                      <span class="cfg-hint">门市价 ¥{{ item.marketPrice }}</span>
                      <a-tag v-if="item.canUpgrade" color="purple" :bordered="false">可升房</a-tag>
                      <a-tag v-if="item.breakfast" :bordered="false">含{{ item.breakfast }}早</a-tag>
                      <a-tag v-for="t in item.discountTexts" :key="'d-' + t" color="gold" :bordered="false">🎟️ {{ t }}</a-tag>
                      <a-tag v-for="t in item.tags.slice(0, 4)" :key="t" :bordered="false">{{ t }}</a-tag>
                      <span class="cfg-hint rate-stock">{{ item.stock >= 3 ? '有房' : '剩' + item.stock + '间' }}</span>
                    </div>
                  </div>
                </template>
              </a-table>
            </a-card>
          </a-col>

          <!-- 右：报价文案 -->
          <a-col :xs="24" :lg="9">
            <a-card :bordered="false" class="panel quote-panel">
              <template #title><span class="panel-title">📝 报价文案</span></template>
              <template #extra>
                <a-button type="primary" :disabled="!rooms.length" @click="copyText">复制文案</a-button>
              </template>
              <a-textarea v-model:value="quoteText" :auto-size="{ minRows: 16, maxRows: 30 }" class="quote-text" />
            </a-card>
          </a-col>
        </a-row>
      </main>
    </div>
  </a-watermark>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import {
  searchPlaces,
  fetchChainDetail,
  fetchChainCoupons,
  checkNewGuestPopup,
  aggregateRooms,
  calcQuote
} from './services/yaduo'

const SEQ = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟']

const DEFAULT_TEMPLATE = `[玫瑰]亲您好，按照您的入住和离开时间，价格如下：{popupTag}

酒店：{hotelName}
地址：{address}
入住：{checkInDate} 至 {checkOutDate}（共{nightCount}晚）

免费赠送：双份自助早餐+延迟退房到下午2点+晚上餐厅深夜粥道

{rooms}

{coupons}`

// ---------- 配置（localStorage 持久化）----------
const LS = {
  queryTokens: 'yd_query_tokens',
  popupTokens: 'yd_popup_tokens',
  pricing: 'yd_pricing',
  template: 'yd_template'
}

const queryTokenText = ref(localStorage.getItem(LS.queryTokens) || '')
const popupTokenText = ref(localStorage.getItem(LS.popupTokens) || '')
const template = ref(localStorage.getItem(LS.template) || DEFAULT_TEMPLATE)
const configOpen = ref(queryTokenText.value ? [] : ['cfg'])

const pricing = reactive(
  JSON.parse(localStorage.getItem(LS.pricing) || 'null') || {
    nonPopupK: 0.85,
    nonPopupB: -10,
    popupK: 0.85,
    popupB: -10
  }
)

const queryTokens = computed(() =>
  queryTokenText.value.split('\n').map((t) => t.trim()).filter(Boolean)
)
const popupTokens = computed(() =>
  popupTokenText.value.split('\n').map((t) => t.trim()).filter(Boolean)
)

watch(queryTokenText, (v) => localStorage.setItem(LS.queryTokens, v))
watch(popupTokenText, (v) => localStorage.setItem(LS.popupTokens, v))
watch(template, (v) => localStorage.setItem(LS.template, v))
watch(pricing, (v) => localStorage.setItem(LS.pricing, JSON.stringify(v)), { deep: true })

// ---------- 查询状态 ----------
const date = ref([dayjs(), dayjs().add(1, 'day')])
const chainId = ref('')
const searchData = ref([])
const searching = ref(false)
const loading = ref(false)

const chainData = reactive({ name: '', address: '' })
const roomUrl = ref('')
const rooms = ref([])
const coupons = ref([])
const hasPopup = ref(false)
const popupChecked = ref(false)
const popupError = ref(false)
const hasQueried = ref(false)

const toggleConfig = () => {
  configOpen.value = configOpen.value.includes('cfg') ? [] : ['cfg']
}

const columns = [
  { title: '房型', key: 'room', width: '34%' },
  { title: '门市价', key: 'market', width: '14%' },
  { title: '最低实价', key: 'lowest', width: '14%' },
  { title: '报价', key: 'quote', width: '14%' },
  { title: '剩余', key: 'stock', width: '12%' },
  { title: '操作', key: 'action', width: '12%' }
]

// ---------- 搜索 ----------
let searchTimer = null
const handleSearch = (kw) => {
  if (!kw) return
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    searching.value = true
    try {
      searchData.value = await searchPlaces(kw)
    } catch (e) {
      message.error('搜索失败：' + (e.message || e))
    } finally {
      searching.value = false
    }
  }, 300)
}

watch(chainId, (v) => {
  const found = searchData.value.find((it) => it.value === v)
  chainData.address = found?.address || ''
  chainData.name = found?.label || ''
})

// ---------- 查价主流程 ----------
const refreshDetail = async () => {
  if (!queryTokens.value.length) {
    message.warning('请先在配置中填写至少一个查价 Token')
    configOpen.value = ['cfg']
    return
  }
  if (!date.value || !date.value[0] || !date.value[1]) {
    message.warning('请选择入住日期')
    return
  }

  loading.value = true
  rooms.value = []
  coupons.value = []
  popupChecked.value = false
  popupError.value = false

  const beginDate = dayjs(date.value[0]).format('YYYY-MM-DD')
  const endDate = dayjs(date.value[1]).format('YYYY-MM-DD')
  const id = chainId.value

  try {
    // 多账号并行查价
    const settled = await Promise.allSettled(
      queryTokens.value.map((token) => fetchChainDetail({ chainId: id, beginDate, endDate, token }))
    )

    const detailResults = []
    let firstResult = null
    settled.forEach((s, i) => {
      if (s.status === 'fulfilled' && s.value?.priceResponse) {
        detailResults.push({ account: `账号${i + 1}`, result: s.value })
        if (!firstResult) firstResult = s.value
      }
    })

    if (!detailResults.length) {
      message.error('所有账号都未查到价格，请检查 Token 是否有效')
      return
    }

    chainData.name = firstResult.chainName || firstResult.priceResponse?.chainName || chainData.name
    chainData.address = firstResult.chainAddress || chainData.address
    roomUrl.value = firstResult.shareUrl || ''
    rooms.value = aggregateRooms(detailResults)

    // 优惠券检测（无需 token）+ 弹窗检测（独立 token）并行
    const [couponRes] = await Promise.allSettled([
      fetchChainCoupons({ chainId: id }),
      runPopupCheck(id)
    ])
    if (couponRes.status === 'fulfilled') coupons.value = couponRes.value

    regenText(beginDate, endDate)
    hasQueried.value = true
    message.success(`查价成功（${detailResults.length}/${queryTokens.value.length} 个账号）`)
  } catch (e) {
    message.error('查价失败：' + (e.message || e))
  } finally {
    loading.value = false
  }
}

const runPopupCheck = async (id) => {
  if (!popupTokens.value.length) {
    popupError.value = true // 未配置 -> 默认非弹窗
    return
  }
  try {
    hasPopup.value = await checkNewGuestPopup({ chainId: id, token: popupTokens.value[0] })
    popupChecked.value = true
  } catch (e) {
    popupError.value = true
    hasPopup.value = false
  }
}

// ---------- 报价计算 ----------
const quoteOf = (room) => calcQuote(room.marketPrice, pricing, hasPopup.value)

// ---------- 文案 ----------
const quoteText = ref('')

const nightCount = computed(() => {
  if (!date.value?.[0] || !date.value?.[1]) return 1
  return Math.max(1, dayjs(date.value[1]).diff(dayjs(date.value[0]), 'day'))
})

const buildRoomsText = () =>
  rooms.value
    .map((r, i) => {
      const stockText = r.stock >= 3 ? '有房' : '剩' + r.stock + '间'
      const up = r.canUpgrade ? '（可升房）' : ''
      return `${SEQ[i] || i + 1 + '.'}${r.roomTypeName}\t\t${quoteOf(r)}元一晚\t\t${stockText}${up}`
    })
    .join('\n')

const buildCouponText = () => {
  const list = coupons.value.filter((c) => c.canGet).map((c) => c.name)
  return list.length ? `💵可领优惠券：${Array.from(new Set(list)).join('、')}` : ''
}

const regenText = (beginDate, endDate) => {
  const map = {
    '{hotelName}': chainData.name || '',
    '{address}': chainData.address || '',
    '{checkInDate}': beginDate,
    '{checkOutDate}': endDate,
    '{nightCount}': String(nightCount.value),
    '{rooms}': buildRoomsText(),
    '{coupons}': buildCouponText(),
    '{popupTag}': hasPopup.value ? '⚠' : ''
  }
  let text = template.value
  for (const k in map) text = text.split(k).join(map[k])
  quoteText.value = text.replace(/\n{3,}/g, '\n\n').trim()
}

// 公式/弹窗状态变化时重算文案
watch(hasPopup, () => {
  if (rooms.value.length) {
    regenText(dayjs(date.value[0]).format('YYYY-MM-DD'), dayjs(date.value[1]).format('YYYY-MM-DD'))
  }
})

const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => message.success('已复制'),
      () => fallbackCopy(text)
    )
  } else {
    fallbackCopy(text)
  }
}
const fallbackCopy = (text) => {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  try {
    document.execCommand('copy')
    message.success('已复制')
  } catch {
    message.error('复制失败，请手动复制')
  }
  document.body.removeChild(ta)
}

const copyText = () => copyToClipboard(quoteText.value)

const copyRoom = (room) => {
  const i = rooms.value.indexOf(room)
  const stockText = room.stock >= 3 ? '有房' : '剩' + room.stock + '间'
  const up = room.canUpgrade ? '（可升房）' : ''
  copyToClipboard(`${SEQ[i] || i + 1 + '.'}${room.roomTypeName}\t\t${quoteOf(room)}元一晚\t\t${stockText}${up}`)
}

const openUrl = (url) => window.open(url)
</script>

<style>
#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  color: #1f2937;
}
.page {
  min-height: 100vh;
  background: #f3f4f6;
}

/* ---------- 顶栏 ---------- */
.topbar {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  position: sticky;
  top: 0;
  z-index: 10;
}
.topbar-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-logo {
  font-size: 26px;
}
.brand-text h1 {
  margin: 0;
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
}
.brand-sub {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
}
.cfg-btn {
  color: #fff !important;
  display: flex;
  align-items: center;
  gap: 4px;
}
.cfg-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
}

/* ---------- 主体 ---------- */
.content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px;
}

/* ---------- 配置 ---------- */
.cfg-collapse {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.cfg-collapse :deep(.ant-collapse-header) {
  font-weight: 600;
}
.cfg-label {
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 13px;
}
.cfg-badge {
  font-weight: 400;
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 4px;
}
.cfg-badge-warn {
  color: #b45309;
  background: #fef3c7;
}
.cfg-hint {
  color: #9ca3af;
  font-size: 12px;
}
.cfg-hint b {
  color: #2563eb;
}
.cfg-hint code,
.cfg-divider code {
  background: #f3f4f6;
  border-radius: 3px;
  padding: 0 4px;
  font-size: 11px;
  color: #2563eb;
}
.cfg-divider {
  margin: 20px 0 16px !important;
  font-size: 13px !important;
  color: #6b7280 !important;
}
.cfg-box {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  height: 100%;
}
.cfg-box-warn {
  border-color: #fde68a;
  background: #fffbeb;
}
.cfg-formula {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.cfg-formula label {
  color: #6b7280;
  font-weight: 600;
}

/* ---------- 查询条 ---------- */
.query-bar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.query-select {
  flex: 1 1 260px;
  min-width: 0;
}
.query-date {
  flex: 0 1 280px;
}
.query-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}
.query-addr {
  margin: 10px 2px 0;
  color: #6b7280;
  font-size: 13px;
}

/* ---------- 优惠券 Banner ---------- */
.coupon-banner {
  margin-top: 16px;
  border-radius: 12px;
}
.coupon-banner-title {
  font-weight: 700;
  font-size: 14px;
}
.coupon-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.coupon-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 13px;
}
.coupon-chip.is-disabled {
  border-color: #e5e7eb;
  opacity: 0.7;
}
.coupon-chip-name {
  font-weight: 600;
}
.coupon-chip-value {
  color: #cf1322;
  font-weight: 700;
}
.coupon-chip-meta {
  color: #9ca3af;
  font-size: 11px;
}

/* ---------- 状态条 ---------- */
.status-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 2px 0;
}

/* ---------- 结果 ---------- */
.result-row {
  margin-top: 16px;
}
.panel {
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.panel-title {
  font-weight: 600;
  font-size: 14px;
}
.room-name {
  display: flex;
  align-items: center;
  gap: 6px;
}
.room-sub {
  color: #9ca3af;
  font-size: 12px;
  margin-top: 2px;
}
.muted {
  color: #9ca3af;
  text-decoration: line-through;
}
.quote-price {
  color: #cf1322;
  font-weight: 700;
}

/* 展开策略列表 */
.rate-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 8px;
}
.rate-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 8px 12px;
}
.rate-name {
  font-weight: 500;
}
.rate-price {
  font-size: 15px;
}
.rate-stock {
  margin-left: auto;
}

/* 报价文案 */
.quote-panel {
  position: sticky;
  top: 76px;
}
.quote-text {
  font-size: 13px;
  line-height: 1.6;
}

/* ---------- 移动端 ---------- */
@media (max-width: 768px) {
  .content {
    padding: 12px;
  }
  .brand-sub {
    display: none;
  }
  .cfg-btn-text {
    display: none;
  }
  .query-bar {
    padding: 12px;
  }
  .query-select,
  .query-date {
    flex: 1 1 100%;
  }
  .query-actions {
    width: 100%;
  }
  .query-actions .ant-btn {
    flex: 1;
  }
  .quote-panel {
    position: static;
  }
  .rate-stock {
    margin-left: 0;
  }
}
</style>
